import { useQuery } from '@apollo/client'
import { Link } from 'react-router-dom'
import { GET_LIVESTREAMS } from '../graphql/operations'
import { useAuth } from '../auth/AuthContext'

interface Livestream {
  id: string
  title: string
  description: string | null
  status: string
  startedAt: string | null
  endedAt: string | null
  viewerCount: number
  duration: number | null
  isLive: boolean
  isUpcoming: boolean
  isEnded: boolean
  thumbnailUrl: string | null
  store: { id: string; name: string; user: { id: string; fullName: string } }
  livestreamProducts: { id: string; position: number | null; isFeatured: boolean; product: { id: string; title: string } }[]
}

interface ListData {
  livestreams: Livestream[]
}

function statusBadge(s: Livestream): { label: string; className: string } {
  if (s.isLive) return { label: 'LIVE', className: 'badge badge-live' }
  if (s.isEnded) return { label: 'Ended', className: 'badge badge-ended' }
  if (s.isUpcoming) return { label: 'Upcoming', className: 'badge badge-upcoming' }
  return { label: s.status, className: 'badge' }
}

export default function StreamList() {
  const { user } = useAuth()
  const { data, loading, error, refetch } = useQuery<ListData>(GET_LIVESTREAMS, {
    variables: { first: 50, sortBy: 'created_at', sortOrder: 'desc' }
  })

  // Filter to streams from the signed-in seller's store. Mall owners /
  // admins see everything in their mall; we just don't have that scope
  // wired here yet — it's a one-line filter swap when the time comes.
  const streams = (data?.livestreams ?? []).filter(s =>
    user?.storeId ? s.store.id === user.storeId : true
  )

  if (loading) return <p className="muted">Loading streams…</p>
  if (error) return <p className="error">{error.message}</p>

  return (
    <div className="stream-list">
      <div className="stream-list-header">
        <h1>Your streams</h1>
        <div>
          <button className="btn-secondary" onClick={() => refetch()}>Refresh</button>
          <Link to="/streams/new" className="btn-primary">Schedule a stream</Link>
        </div>
      </div>

      {streams.length === 0 && (
        <div className="empty-state">
          <p>No streams yet.</p>
          <Link to="/streams/new" className="btn-primary">Create your first one</Link>
        </div>
      )}

      <ul className="stream-grid">
        {streams.map(s => {
          const badge = statusBadge(s)
          return (
            <li key={s.id} className="stream-card">
              <div className="stream-card-thumb">
                {s.thumbnailUrl ? (
                  <img src={s.thumbnailUrl} alt={s.title} />
                ) : (
                  <div className="stream-card-thumb-placeholder">📹</div>
                )}
                <span className={badge.className}>{badge.label}</span>
              </div>
              <div className="stream-card-body">
                <h3>{s.title}</h3>
                <p className="muted small">{s.livestreamProducts.length} products · {s.viewerCount} viewers</p>
                <div className="stream-card-actions">
                  <Link to={`/streams/${s.id}/go-live`} className="btn-primary">
                    {s.isLive ? 'Rejoin' : 'Go live'}
                  </Link>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
